import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Tabs, Tab } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { HttpService } from "../../service/HttpService";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import { useAuth } from "../../context/AuthProvider";
import "./RoomLogReport.css";

// ─── Two-color palette ────────────────────────────────────────────────────────
const COLOR_TARGET = "#00A272"; // green  → target line
const COLOR_AVG = "#2979FF"; // blue   → average line

// ─── 14 chart configurations ──────────────────────────────────────────────────
const CHART_CONFIGS = [
  // 1. Day Temperature
  {
    title: "Day Temperature (°F)",
    lines: [
      { key: "day_temp_target", label: "Day Temp Target", color: COLOR_TARGET },
      { key: "avg_day_temp", label: "Avg Day Temp", color: COLOR_AVG },
    ],
  },
  // 2. Night Temperature
  {
    title: "Night Temperature (°F)",
    lines: [
      {
        key: "night_temp_target",
        label: "Night Temp Target",
        color: COLOR_TARGET,
      },
      { key: "avg_night_temp", label: "Avg Night Temp", color: COLOR_AVG },
    ],
  },
  // 3. Day Humidity
  {
    title: "Day Humidity (%)",
    lines: [
      {
        key: "day_humidity_target",
        label: "Day Humidity Target",
        color: COLOR_TARGET,
      },
      { key: "avg_day_humidity", label: "Avg Day Humidity", color: COLOR_AVG },
    ],
  },
  // 4. Night Humidity
  {
    title: "Night Humidity (%)",
    lines: [
      {
        key: "night_humidity_target",
        label: "Night Humidity Target",
        color: COLOR_TARGET,
      },
      {
        key: "avg_night_humidity",
        label: "Avg Night Humidity",
        color: COLOR_AVG,
      },
    ],
  },
  // 5. CO₂
  {
    title: "CO₂ (ppm)",
    lines: [
      { key: "co2_target", label: "CO₂ Target", color: COLOR_TARGET },
      { key: "avg_co2", label: "Avg CO₂", color: COLOR_AVG },
    ],
  },
  // 6. Water Temp – Epi 01
  {
    title: "Water Temp – Epi 01 (°F)",
    lines: [
      {
        key: "water_temp_target",
        label: "Water Temp Target",
        color: COLOR_TARGET,
      },
      {
        key: "epi01_water_temp_avg",
        label: "Epi 01 Water Temp Avg",
        color: COLOR_AVG,
      },
    ],
  },
  // 7. Water Temp – Epi 02
  {
    title: "Water Temp – Epi 02 (°F)",
    lines: [
      {
        key: "water_temp_target",
        label: "Water Temp Target",
        color: COLOR_TARGET,
      },
      {
        key: "epi02_water_temp_avg",
        label: "Epi 02 Water Temp Avg",
        color: COLOR_AVG,
      },
    ],
  },
  // 8. Water Temp – Epi 03
  {
    title: "Water Temp – Epi 03 (°F)",
    lines: [
      {
        key: "water_temp_target",
        label: "Water Temp Target",
        color: COLOR_TARGET,
      },
      {
        key: "epi03_water_temp_avg",
        label: "Epi 03 Water Temp Avg",
        color: COLOR_AVG,
      },
    ],
  },
  // 9. pH – Epi 01
  {
    title: "pH – Epi 01",
    lines: [
      { key: "ph_target", label: "pH Target", color: COLOR_TARGET },
      { key: "epi01_ph_avg", label: "Epi 01 pH Avg", color: COLOR_AVG },
    ],
  },
  // 10. pH – Epi 02
  {
    title: "pH – Epi 02",
    lines: [
      { key: "ph_target", label: "pH Target", color: COLOR_TARGET },
      { key: "epi02_ph_avg", label: "Epi 02 pH Avg", color: COLOR_AVG },
    ],
  },
  // 11. pH – Epi 03
  {
    title: "pH – Epi 03",
    lines: [
      { key: "ph_target", label: "pH Target", color: COLOR_TARGET },
      { key: "epi03_ph_avg", label: "Epi 03 pH Avg", color: COLOR_AVG },
    ],
  },
  // 12. TDS – Epi 01
  {
    title: "TDS – Epi 01 (ppm)",
    lines: [
      { key: "tds_target", label: "TDS Target", color: COLOR_TARGET },
      { key: "epi01_tds_avg", label: "Epi 01 TDS Avg", color: COLOR_AVG },
    ],
  },
  // 13. TDS – Epi 02
  {
    title: "TDS – Epi 02 (ppm)",
    lines: [
      { key: "tds_target", label: "TDS Target", color: COLOR_TARGET },
      { key: "epi02_tds_avg", label: "Epi 02 TDS Avg", color: COLOR_AVG },
    ],
  },
  // 14. TDS – Epi 03
  {
    title: "TDS – Epi 03 (ppm)",
    lines: [
      { key: "tds_target", label: "TDS Target", color: COLOR_TARGET },
      { key: "epi03_tds_avg", label: "Epi 03 TDS Avg", color: COLOR_AVG },
    ],
  },
  // 15. Light Level
  {
    title: "Light Level",
    lines: [
      { key: "avg_light_level", label: "Avg Light Level", color: COLOR_AVG },
    ],
  },
];

// ─── Shared MUI Tabs styling (same as RoomTabs.js) ───────────────────────────
const tabsSx = {
  backgroundColor: "#fffffc",
  "& .MuiTabs-indicator": { backgroundColor: "#00A272", height: "3px" },
  "& .MuiTab-root": {
    position: "relative",
    color: "#A1A2A1",
    fontWeight: 400,
    fontSize: "16px",
    textTransform: "none",
    padding: "12px 20px",
    marginRight: "8px",
    "&:not(.Mui-selected)::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: "3px",
      backgroundColor: "#C4C4C4",
    },
    "&.Mui-selected": {
      color: "#00A272 !important",
      fontWeight: 600,
      backgroundColor: "#E6F6F1",
      borderRadius: "4px 4px 0 0",
    },
  },
};

// ─── Custom tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rlr-tooltip">
      <p className="rlr-tooltip-label">{label}</p>
      {payload.map((entry) => (
        <p
          key={entry.dataKey}
          style={{ color: entry.color }}
          className="rlr-tooltip-row"
        >
          <span
            className="rlr-tooltip-dot"
            style={{ backgroundColor: entry.color }}
          />
          {entry.name}:{" "}
          <strong>
            {entry.value !== null && entry.value !== undefined
              ? Number(entry.value).toFixed(2)
              : "—"}
          </strong>
        </p>
      ))}
    </div>
  );
};

// ─── Single chart card ────────────────────────────────────────────────────────
const ChartCard = ({ config, data }) => {
  const hasData = data && data.length > 0;

  // Compute a symmetric domain so lines sit in the vertical centre of the chart.
  // Collect every numeric value rendered by this chart, find min/max,
  // then add 50 % of the range as equal padding on both sides.
  const yAxis = useMemo(() => {
    if (!hasData) return { domain: ["auto", "auto"], ticks: undefined };

    const values = [];
    data.forEach((row) => {
      config.lines.forEach((line) => {
        const v = Number(row[line.key]);
        if (!isNaN(v)) values.push(v);
      });
    });

    if (values.length === 0)
      return { domain: ["auto", "auto"], ticks: undefined };

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const center = (min + max) / 2;

    // Chart range = 2 × data range → data occupies the middle 50%
    let rawLower = center - range; // = min - range/2
    let rawUpper = center + range; // = max + range/2

    // When lower dips below 0, re-anchor at 0 and balance upper
    // so the data midpoint stays at the 50% mark of [0, upper]
    if (rawLower < 0) {
      rawLower = 0;
      rawUpper = center * 2; // keeps data centre at upper/2
    }

    const visibleRange = rawUpper - rawLower || 1;

    // "Nice numbers" algorithm → target ≈ 6 evenly-spaced ticks
    const roughStep = visibleRange / 6;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep || 1)));
    const n = roughStep / magnitude;
    let step = magnitude;
    if (n > 7.5) step = 10 * magnitude;
    else if (n > 3) step = 5 * magnitude;
    else if (n > 1.5) step = 2 * magnitude;

    // Whole-number minimum (prevents sub-1 steps that look like decimals)
    step = Math.max(1, Math.round(step));

    const lower = Math.max(0, Math.floor(rawLower / step) * step);
    const upper = Math.ceil(rawUpper / step) * step;

    // Build tick array
    const ticks = [];
    for (let t = lower; t <= upper; t += step) ticks.push(t);

    return { domain: [lower, upper], ticks };
  }, [data, config]);

  return (
    <div className="rlr-chart-card">
      <h3 className="rlr-chart-title">{config.title}</h3>
      {!hasData ? (
        <div className="rlr-no-data">No data available</div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart
            data={data}
            margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF2" />
            <XAxis
              dataKey="growth_phase"
              tick={{ fontSize: 11, fill: "#555" }}
              interval={0}
              angle={-18}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#555" }}
              width={52}
              domain={yAxis.domain}
              ticks={yAxis.ticks}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }}
              iconType="circle"
            />
            {config.lines.map((line) => (
              <Line
                key={line.key}
                type="monotone"
                dataKey={line.key}
                name={line.label}
                stroke={line.color}
                strokeWidth={2}
                dot={{ r: 3, fill: line.color }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const RoomLogReport = () => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);

  const [rooms, setRooms] = useState([]);
  const [roomTab, setRoomTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);

  // ── fetch rooms (same filtering/sorting as RoomTabs.js) ───────────────────
  const fetchRooms = useCallback(async () => {
    try {
      const res = await httpService.get("rooms?sortBy=name&sortOrder=ASC");
      const roomsData = res?.data?.data || [];
      const EXTRA_ROOMS = ["clone room", "mom room"];
      const filtered = roomsData
        .filter((room) => {
          if (!room.name) return false;
          const name = room.name.trim().toLowerCase();
          return (
            /^room\s([1-9][0-9]?)$/.test(name) || EXTRA_ROOMS.includes(name)
          );
        })
        .sort((a, b) => {
          const getNumber = (name) => {
            const match = name.match(/\d+/);
            return match ? Number(match[0]) : Infinity;
          };
          return getNumber(a.name) - getNumber(b.name);
        });
      setRooms(filtered);
    } catch {
      setRooms([]);
    }
  }, []);

  // ── fetch archive data for the selected room ──────────────────────────────
  const fetchArchiveData = useCallback(async (roomId) => {
    if (!roomId) return;
    setLoading(true);
    try {
      // The archive endpoint returns rows ordered by growth_phase_id.
      // We fetch with no harvested_date filter so all archives are returned,
      // then group/average them by growth phase name for the chart.
      const res = await httpService.get(`room-logs/latest-archive/${roomId}`);
      const raw = Array.isArray(res?.data) ? res.data : [];

      // Group by growth_phase_name and average numeric values
      const grouped = {};
      raw.forEach((row) => {
        const phase = row.growth_phase_name || "Unknown";
        if (!grouped[phase]) {
          grouped[phase] = {
            count: 0,
            sums: {},
            growth_phase_id: row.growth_phase_id,
          };
        }
        const g = grouped[phase];
        g.count += 1;
        const numericKeys = [
          "day_temp_target",
          "average_day_temp",
          "night_temp_target",
          "average_night_temp",
          "day_humidity_target",
          "average_day_humidity",
          "night_humidity_target",
          "average_night_humidity",
          "ph_target",
          "epi_01_ph_avg",
          "epi_02_ph_avg",
          "epi_03_ph_avg",
          "tds_target",
          "epi_01_tds_avg",
          "epi_02_tds_avg",
          "epi_03_tds_avg",
          "water_temp_target",
          "epi_01_water_temp_avg",
          "epi_02_water_temp_avg",
          "epi_03_water_temp_avg",
          "average_light_level_avg",
          "co2_target",
          "average_co2_avg",
        ];
        numericKeys.forEach((k) => {
          const v = parseFloat(row[k]);
          g.sums[k] = (g.sums[k] || 0) + (isNaN(v) ? 0 : v);
        });
      });

      // Sort by growth_phase_id so chart x-axis follows natural growth order
      const sorted = Object.entries(grouped)
        .sort((a, b) => a[1].growth_phase_id - b[1].growth_phase_id)
        .map(([phase, g]) => {
          const avg = (k) =>
            g.count ? +(g.sums[k] / g.count).toFixed(3) : null;
          return {
            growth_phase: phase,
            day_temp_target: avg("day_temp_target"),
            avg_day_temp: avg("average_day_temp"),
            night_temp_target: avg("night_temp_target"),
            avg_night_temp: avg("average_night_temp"),
            day_humidity_target: avg("day_humidity_target"),
            avg_day_humidity: avg("average_day_humidity"),
            night_humidity_target: avg("night_humidity_target"),
            avg_night_humidity: avg("average_night_humidity"),
            ph_target: avg("ph_target"),
            epi01_ph_avg: avg("epi_01_ph_avg"),
            epi02_ph_avg: avg("epi_02_ph_avg"),
            epi03_ph_avg: avg("epi_03_ph_avg"),
            tds_target: avg("tds_target"),
            epi01_tds_avg: avg("epi_01_tds_avg"),
            epi02_tds_avg: avg("epi_02_tds_avg"),
            epi03_tds_avg: avg("epi_03_tds_avg"),
            water_temp_target: avg("water_temp_target"),
            epi01_water_temp_avg: avg("epi_01_water_temp_avg"),
            epi02_water_temp_avg: avg("epi_02_water_temp_avg"),
            epi03_water_temp_avg: avg("epi_03_water_temp_avg"),
            avg_light_level: avg("average_light_level_avg"),
            co2_target: avg("co2_target"),
            avg_co2: avg("average_co2_avg"),
          };
        });

      setChartData(sorted);
    } catch {
      setChartData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch rooms on mount
  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // When rooms load: auto-select the first room that has an archive and fetch its data
  useEffect(() => {
    if (rooms.length === 0) return;
    const idx = rooms.findIndex((r) => r.has_archive === true);
    const targetIdx = idx !== -1 ? idx : 0;
    setRoomTab(targetIdx);
    if (rooms[targetIdx]) {
      fetchArchiveData(rooms[targetIdx].id);
    }
  }, [rooms]);

  // Handler for manual tab click: update state and fetch in one shot
  const handleRoomChange = useCallback(
    (_, newIdx) => {
      setRoomTab(newIdx);
      if (rooms[newIdx]) {
        fetchArchiveData(rooms[newIdx].id);
      }
    },
    [rooms, fetchArchiveData]
  );

  return (
    <div className="company">
      {loading && <FullScreenLoader />}

      {/* Page header */}
      <div className="page-title">
        <div className="title-wrap">
          <h1>Room Log Report</h1>
        </div>
      </div>

      <div className="common_content">
        {/* Room tabs */}
        <Tabs
          value={roomTab}
          onChange={handleRoomChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{ ...tabsSx, mb: 3 }}
        >
          {rooms.map((room) => (
            <Tab
              key={room.id}
              label={room.name}
              disabled={!room.has_archive}
              sx={{
                "&.Mui-disabled": {
                  color: "#A1A2A1",
                  opacity: 0.5,
                },
              }}
            />
          ))}
        </Tabs>

        {/* Charts grid */}
        {rooms.length === 0 ? (
          <div className="rlr-empty-state">No rooms found.</div>
        ) : (
          <div className="rlr-charts-grid">
            {CHART_CONFIGS.map((cfg) => (
              <ChartCard key={cfg.title} config={cfg} data={chartData} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomLogReport;
